// Components/RecordEvent.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Web3 from 'web3';
import { hashFile, hashEvent } from '../utils/hashUtils';

const apiUrl = process.env.REACT_APP_API_URL;

const RecordEvent = ({ tokenId: passedTokenId, hideTokenId = false }) => {
  const [tokenId, setTokenId] = useState(passedTokenId || '');
  const [eventName, setEventName] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [file, setFile] = useState(null);
  const [eventAddress, setEventAddress] = useState('');
  const [tokens, setTokens] = useState([]);
  const [recordedEvents, setRecordedEvents] = useState([]); // Track recorded event names
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editMode, setEditMode] = useState(false); // Flag for editing address

  // Fetch all tokens if not hiding token selection
  useEffect(() => {
    if (!hideTokenId) {
      const getTokens = async () => {
        try {
          const response = await axios.get(`${apiUrl}/api/tokens`);
          if (response.status === 200) {
            setTokens(response.data);
          } else {
            throw new Error('Failed to fetch tokens');
          }
        } catch (err) {
          setError('Failed to fetch tokens');
        }
      };
      getTokens();
    }
  }, [hideTokenId]);

  // Fetch recorded events for the selected token
  useEffect(() => {
    if (tokenId) {
      const fetchRecordedEvents = async () => {
        try {
          const response = await axios.get(`${apiUrl}/api/tokens/${tokenId}`);
          if (response.status === 200) {
            const tokenData = response.data;
            const events = tokenData.events || [];
            setRecordedEvents(events.map((event) => event.eventName));
          } else {
            throw new Error('Failed to fetch recorded events');
          }
        } catch (err) {
          setError('Failed to fetch recorded events');
        }
      };
      fetchRecordedEvents();
    }
  }, [tokenId]);

  // Fetch current location
    const fetchLocation = async () => {
      if (!navigator.geolocation) {
        setError('Geolocation is not supported by your browser');
        return;
      }
  
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          
          console.log("Latitude:", latitude);
          console.log("Longitude:", longitude);

          try {
            const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
            const response = await axios.get(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`
            );
  
            if (response.data.status === 'OK') {
              const formattedAddress = response.data.results[0].formatted_address;
              setEventAddress(formattedAddress);
            } else {
              setError('Failed to retrieve address');
            }
          } catch (error) {
            console.error('Error reverse geocoding:', error);
            setError('Error retrieving address');
          }
        },
        (error) => {
          console.error('Error fetching location:', error);
          setError('Unable to fetch your location');
        }
      );
    };

  useEffect(() => {
      fetchLocation();
    }, []);
  
    const handleFileChange = (event) => {
      setFile(event.target.files[0]);
    };
  
    const handleConfirmAddress = async () => {
      try {
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        const response = await axios.get(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(eventAddress)}&key=${apiKey}`
        );
  
        if (response.data.status === 'OK') {
          const confirmedAddress = response.data.results[0].formatted_address;
          setEventAddress(confirmedAddress);
          setEditMode(false);
          console.log('Address confirmed:', confirmedAddress);
        } else {
          setError('Unable to confirm the address.');
        }
      } catch (error) {
        console.error('Error confirming address:', error);
        setError('Error verifying the address');
      }
    };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const currentTokenId = hideTokenId ? passedTokenId : tokenId;

    if (!currentTokenId || !eventName || !eventDescription || !eventAddress ||!file) {
      alert('Please fill in all fields.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const web3 = new Web3(window.ethereum);
      await window.ethereum.request({ method: 'eth_requestAccounts' });

      const fileHash = await hashFile(file);
      const eventMetadataHash = await hashEvent(
        currentTokenId,
        eventName,
        eventDescription,
        eventAddress,
        fileHash
      );

      const eventHash = web3.utils.toHex(eventMetadataHash);
      // console.log('frontend file hash', fileHash)
      // console.log("Combined data for event hash in frontend:", eventHash);

      const accounts = await web3.eth.getAccounts();
      const signature = await web3.eth.personal.sign(eventHash, accounts[0],'');
      const tokenId = currentTokenId;

      const formData = new FormData();
      formData.append('tokenId', tokenId);
      formData.append('eventName', eventName);
      formData.append('eventDescription', eventDescription);
      formData.append('eventAddress', eventAddress);
      formData.append('frontendEventHash', eventHash);
      formData.append('signature', signature);
      formData.append('file', file);

      console.log('Form Data:', formData);

      const response = await axios.post(`${apiUrl}/api/events/recordEvent`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      console.log('Server response:', response.data);
      setSuccess('Event recorded successfully!');
    } catch (error) {
      console.error('Error recording event:', error);
      setError('An error occurred while recording the event.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2>Record Provenance by Event</h2>
      <form onSubmit={handleSubmit}>
        {!hideTokenId && (
          <div>
            <label htmlFor="tokenId">Passport ID:</label>
            <select
              id="tokenId"
              value={tokenId}
              onChange={(event) => setTokenId(event.target.value)}
              required
            >
              <option value="" disabled>
                Select a Product Passport
              </option>

              {Array.isArray(tokens) &&
                tokens.map((token) => (
                  <option
                    key={token.tokenId}
                    value={token.tokenId}
                  >
                    #{token.tokenId}
                    {' — '}
                    {token.name || 'Unnamed Product Passport'}
                  </option>
                ))}
            </select>
          </div>
        )}

        <div>
          <label htmlFor="eventName">Event Name:</label>
          <select
            id="eventName"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            required
            disabled={!tokenId}
          >
            <option value="" disabled>Select an event</option>
            {['Growing', 'Harvesting', 'Processing', 'Packaging', 'Shipping', 'Delivery'].map((event) => (
              <option
                key={event}
                value={event}
                disabled={tokenId && recordedEvents.includes(event)}
                style={{
                  color: tokenId && recordedEvents.includes(event) ? 'grey' : 'black',
                }}
              >
                {event}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="eventDescription">Event Description:</label>
          <textarea
            id="eventDescription"
            value={eventDescription}
            onChange={(e) => setEventDescription(e.target.value)}
            required
          />
        </div>

        <div>
          <label htmlFor="file">Event File:</label>
          <input
            type="file"
            id="file"
            onChange={handleFileChange}
            required
          />
        </div>

        <div>
          <label htmlFor="address">Event Location:</label>
          <input
            type="text"
            id="address"
            value={eventAddress}
            onChange={(e) => setEventAddress(e.target.value)}
            disabled={!editMode}
          />
          {!editMode ? (
            <button type="button" onClick={() => setEditMode(true)} style={{ marginTop: '0px' }}
            >Edit Address</button>
          ) : (
            <>
              <button type="button" onClick={handleConfirmAddress}>Confirm Address</button>
              <button type="button" onClick={() => setEditMode(false)}>Cancel</button>
            </>
          )}
        </div>

        <div style={{ marginTop: '5px' }}>
          {loading ? (
            <p>Loading...</p>
          ) : (
            <button type="submit" style={{ marginBottom: "40px" }}>
              Record Provenance
            </button>
          )}
        </div>

        {success && <p style={{ color: 'green' }}>{success}</p>}
        {error && <p style={{ color: 'red' }}>{error}</p>}
      </form>
    </div>
  );
};

export default RecordEvent;